import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useSnackbar } from "notistack";
import { useTheme } from "@mui/material/styles";
import { SystemDialog } from "../../../utils/Dialog";
import { getFormattedDate, getReadableShortDate } from "../../../utils/commons";
import {
  getTodaysTransformationWod,
  saveTransformationWod,
  uploadTransformationWodImage,
} from "../../../services/TransformationWodService";
import {
  hasWodImage,
  resolveWodImage,
} from "../../../services/AssetConfigService";
import { TransformationWOD } from "./types";

interface ICreateTransformationWOD {
  editingWod?: TransformationWOD;
  refreshCounter?: number;
  assetBaseUrl?: string;
  onSaved: () => void;
  onCancelEdit: () => void;
}

export const CreateTransformationWOD = ({
  editingWod,
  refreshCounter,
  assetBaseUrl,
  onSaved,
  onCancelEdit,
}: ICreateTransformationWOD) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // Data URL of a newly picked file; existing images are resolved from the doc.
  const [localPreviewUrl, setLocalPreviewUrl] = useState("");
  const [todaysWod, setTodaysWod] = useState<TransformationWOD | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const isEditing = Boolean(editingWod);
  const displayDate = useMemo(
    () => (editingWod?.date ? editingWod.date : getFormattedDate()),
    [editingWod?.date]
  );
  const hasExistingImage = hasWodImage(editingWod);
  const existingPreviewUrl = editingWod
    ? resolveWodImage(editingWod, assetBaseUrl)
    : "";
  const previewUrl = localPreviewUrl || existingPreviewUrl;
  const hasImage = Boolean(selectedFile) || hasExistingImage;

  useEffect(() => {
    // Starting, switching or cancelling an edit discards any picked file.
    setSelectedFile(null);
    setLocalPreviewUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    if (editingWod) {
      return;
    }

    getTodaysTransformationWod()
      .then((wod) => setTodaysWod(wod))
      .catch(() => setTodaysWod(null));
  }, [editingWod, refreshCounter]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      return;
    }

    // Select immediately so Save validation doesn't wait on the preview.
    setSelectedFile(file);
    setLocalPreviewUrl("");

    const reader = new FileReader();
    reader.onloadend = () => {
      // Ignore a slow read for a file that has since been replaced.
      if (fileInputRef.current?.files?.[0] === file) {
        setLocalPreviewUrl(reader.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  const resetUploadState = () => {
    setSelectedFile(null);
    setLocalPreviewUrl("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSaveClicked = () => {
    if (!hasImage) {
      enqueueSnackbar("Transformation image is required", {
        variant: "warning",
      });
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleSaveConfirm = async () => {
    try {
      setIsUploading(true);

      const imageKey = selectedFile
        ? await uploadTransformationWodImage(selectedFile)
        : editingWod?.imageKey || "";

      if (!imageKey) {
        enqueueSnackbar("Transformation image is required", {
          variant: "warning",
        });
        setIsUploading(false);
        setShowConfirmDialog(false);
        return;
      }

      await saveTransformationWod(displayDate, imageKey);

      enqueueSnackbar("Transformation WOD saved successfully!");
      resetUploadState();
      setShowConfirmDialog(false);
      setIsUploading(false);

      if (!isEditing) {
        setTodaysWod({ date: displayDate, imageKey });
      }

      onSaved();
    } catch (_e) {
      setIsUploading(false);
      setShowConfirmDialog(false);
      enqueueSnackbar("Failed to save Transformation WOD", {
        variant: "error",
      });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          mb: 1.5,
          flexShrink: 0,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ color: theme.palette.secondary.main }}
        >
          {isEditing
            ? `Editing: ${getReadableShortDate(displayDate)}`
            : `Today: ${getReadableShortDate()}`}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isEditing && (
            <Button
              color="inherit"
              variant="outlined"
              size="small"
              startIcon={<CancelIcon />}
              onClick={onCancelEdit}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
            >
              Cancel Edit
            </Button>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 2,
          maxWidth: 760,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg, image/jpg, image/png"
            onChange={handleFileInputChange}
            disabled={isUploading}
            style={{ display: "none" }}
            id="transformation-wod-image-upload"
          />
          <label
            htmlFor="transformation-wod-image-upload"
            style={{ flex: 1, minWidth: 280 }}
          >
            <Box
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                width: "100%",
                minHeight: 52,
                px: 1.5,
                py: 0.75,
                borderRadius: 1.5,
                border: "1px dashed",
                borderColor: hasImage ? "secondary.main" : "primary.main",
                backgroundColor: "background.paper",
                cursor: isUploading ? "not-allowed" : "pointer",
                opacity: isUploading ? 0.7 : 1,
                transition: "border-color 0.2s ease",
              }}
            >
              {previewUrl ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Transformation WOD"
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 1,
                    objectFit: "cover",
                    flexShrink: 0,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                />
              ) : null}

              <Typography
                variant="body2"
                sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
              >
                {hasImage
                  ? selectedFile?.name || "Image selected. Click to change"
                  : "Choose transformation image (JPG/PNG)"}
              </Typography>
            </Box>
          </label>

          <Button
            color="secondary"
            endIcon={<SendIcon />}
            variant="contained"
            disabled={isUploading}
            onClick={handleSaveClicked}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1.5,
              minHeight: 52,
              px: 2,
            }}
          >
            {isUploading ? "Uploading..." : "Save Transformation WOD"}
          </Button>
        </Box>

        {!isEditing && hasWodImage(todaysWod) && !selectedFile && (
          <Typography variant="caption" color="text.secondary">
            Existing image found for today. Saving will overwrite it.
          </Typography>
        )}
      </Box>

      <SystemDialog
        component={
          <Typography variant="overline">
            {isEditing
              ? "Are you sure you want to update this transformation workout?"
              : "Are you sure you want to save today's transformation workout?"}
          </Typography>
        }
        open={showConfirmDialog}
        handleClose={() => setShowConfirmDialog(false)}
        handleConfirm={handleSaveConfirm}
        heading="Save Transformation Workout"
      />
    </Box>
  );
};
